/**
 * CommunicationsTab — SMS / call / email history for the property.
 *
 * Reads the immutable property timeline and filters to communication-
 * class events. No new backend endpoint needed — everything's already
 * logged by the property SMS endpoint, Twilio webhooks, and outreach
 * sequences.
 *
 * "Log a note" is a quick-add UI on top of the SMS endpoint pattern:
 * POSTing an event via /v3/properties/{id}/sms with type=note.
 */
import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, MessageSquare, PhoneCall, Mail, StickyNote, Filter, Send,
  Sparkles, Hand, Play,
} from "lucide-react";

// Timeline event codes we consider "communications" for filtering.
const COMM_CODES = new Set([
  "communication_logged",
  "sms_sent", "sms_received",
  "call_started", "call_completed", "call_missed",
  "email_sent", "email_received",
  "note_added",
]);

const ICON_BY_KIND = {
  sms: MessageSquare,
  call: PhoneCall,
  email: Mail,
  note: StickyNote,
};

function kindFromEvent(ev) {
  const code = String(ev.event_code || "").toLowerCase();
  if (code.startsWith("sms")) return "sms";
  if (code.startsWith("call")) return "call";
  if (code.startsWith("email")) return "email";
  if (code.startsWith("note")) return "note";
  const t = String(ev.payload?.type || ev.payload?.channel || "").toLowerCase();
  if (t.includes("sms")) return "sms";
  if (t.includes("call")) return "call";
  if (t.includes("email")) return "email";
  return "note";
}

export default function CommunicationsTab({ property }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | sms | call | email | note
  const [noteText, setNoteText] = useState("");
  const [posting, setPosting] = useState(false);
  const [autopilot, setAutopilot] = useState(null);   // {active, paused_at, ...}
  const [readiness, setReadiness] = useState(null);   // {all_green, checks}
  const [togglingAutopilot, setTogglingAutopilot] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(
        `/v3/properties/${property.id}/timeline?limit=200`,
      );
      const items = (data.items || []).filter(
        (ev) => COMM_CODES.has(String(ev.event_code || "").toLowerCase()),
      );
      setEvents(items);
    } catch {
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [property.id]);
  useEffect(() => { load(); }, [load]);

  // Load Jarvis autopilot + readiness state for the strip at the top.
  const loadAutopilot = useCallback(async () => {
    const [a, r] = await Promise.all([
      api.get(`/v3/ai-sdr/autopilot/${property.id}`).then((x) => x.data).catch(() => null),
      api.get("/v3/ai-sdr/readiness").then((x) => x.data).catch(() => null),
    ]);
    if (a) setAutopilot(a);
    if (r) setReadiness(r);
  }, [property.id]);
  useEffect(() => { loadAutopilot(); }, [loadAutopilot]);

  const toggleAutopilot = async (nextActive) => {
    setTogglingAutopilot(true);
    try {
      const { data } = await api.post(
        `/v3/ai-sdr/autopilot/${property.id}`, { active: nextActive },
      );
      setAutopilot(data);
      toast.success(nextActive ? "Jarvis is replying again" : "You've taken over — Jarvis paused");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't update autopilot");
    } finally {
      setTogglingAutopilot(false);
    }
  };

  const postNote = async () => {
    if (!noteText.trim()) return;
    setPosting(true);
    try {
      await api.post(`/v3/properties/${property.id}/sms`, {
        message: noteText.trim(), type: "note", to: "",
      });
      setNoteText("");
      toast.success("Note logged");
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't log note");
    } finally {
      setPosting(false);
    }
  };

  const filtered = filter === "all"
    ? events
    : events.filter((ev) => kindFromEvent(ev) === filter);

  return (
    <div className="space-y-4" data-testid="tab-panel-communications">
      {/* Jarvis Autopilot strip — Take Over / Resume */}
      <JarvisAutopilotStrip
        autopilot={autopilot}
        readiness={readiness}
        toggling={togglingAutopilot}
        onToggle={toggleAutopilot}
        events={events}
      />

      {/* Quick note logger */}
      <div className="rounded-xl border border-slate-200 bg-white p-3 flex items-center gap-2">
        <StickyNote className="w-4 h-4 text-slate-400 shrink-0" />
        <input
          type="text" value={noteText}
          onChange={(e) => setNoteText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") postNote(); }}
          placeholder="Log a call, meeting, or quick note…"
          data-testid="comm-note-input"
          className="flex-1 rounded-md border border-slate-200 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400"
        />
        <button
          type="button" onClick={postNote} disabled={posting || !noteText.trim()}
          data-testid="comm-note-submit"
          className="inline-flex items-center gap-1 rounded-md bg-slate-900 text-white text-[11.5px] font-semibold px-2.5 py-1.5 hover:bg-slate-800 disabled:opacity-50"
        >
          {posting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />} Log
        </button>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-1.5 flex-wrap">
        <Filter className="w-3.5 h-3.5 text-slate-400" />
        {["all", "sms", "call", "email", "note"].map((k) => (
          <button
            key={k} type="button" onClick={() => setFilter(k)}
            data-testid={`comm-filter-${k}`}
            className={
              "text-[11px] font-semibold uppercase tracking-widest px-2 py-1 rounded-md " +
              (filter === k
                ? "bg-slate-900 text-white"
                : "text-slate-600 hover:bg-slate-100")
            }
          >{k}</button>
        ))}
      </div>

      {/* Timeline list */}
      {loading ? (
        <div className="flex items-center gap-2 text-slate-600 text-sm" data-testid="comm-loading">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading communications…
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center"
             data-testid="comm-empty">
          <MessageSquare className="w-6 h-6 text-slate-400 mx-auto mb-2" />
          <div className="text-slate-900 font-semibold text-sm">No communications yet</div>
          <div className="text-[12px] text-slate-500 mt-1">
            Calls, SMS, emails, and notes on this property will appear here.
          </div>
        </div>
      ) : (
        <ul className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100">
          {filtered.map((ev, i) => {
            const kind = kindFromEvent(ev);
            const Icon = ICON_BY_KIND[kind] || StickyNote;
            return (
              <li key={ev.id || i}
                  data-testid={`comm-event-${i}`}
                  className="px-4 py-3 flex items-start gap-3">
                <span className="rounded-md bg-amber-50 border border-amber-100 p-1.5 shrink-0">
                  <Icon className="w-3.5 h-3.5 text-amber-700" />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-[12.5px] font-semibold text-slate-900 flex items-center gap-2 flex-wrap">
                    <span>{ev.label || ev.event_code}</span>
                    <span className="text-[10px] uppercase tracking-widest font-black text-slate-400">{kind}</span>
                    {ev.payload?.ai_sdr && (
                      <span
                        data-testid={`comm-event-${i}-jarvis-badge`}
                        className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[9.5px] font-black uppercase tracking-widest text-amber-800"
                      >
                        <Sparkles className="w-2.5 h-2.5" />
                        Jarvis · {ev.payload.persona_name || "Auto"}
                      </span>
                    )}
                  </div>
                  {(ev.payload?.body_preview || ev.payload?.message) && (
                    <div className="text-[12.5px] text-slate-700 mt-0.5 whitespace-pre-wrap">
                      {ev.payload.body_preview || ev.payload.message}
                    </div>
                  )}
                  <div className="text-[10.5px] text-slate-400 mt-1">
                    {(ev.occurred_at || ev.created_at || "")
                      .replace("T", " ").slice(0, 16) || "—"}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}


// ─────────────────────────────────────── Jarvis Autopilot Strip ───────────
// Sits at the top of the Comms tab and shows whether Jarvis is currently
// handling this seller's replies. Two controls: "Take Over" (pauses
// autonomy for THIS lead only) and "Resume Jarvis". Reads readiness so
// the operator understands why Jarvis might be silent even when active.

function JarvisAutopilotStrip({ autopilot, readiness, toggling, onToggle, events }) {
  if (!autopilot || !readiness) return null;
  const allGreen = !!readiness.all_green;
  const active = autopilot.active !== false;
  const lastJarvisEvent = (events || []).find((e) => e?.payload?.ai_sdr);
  const persona = lastJarvisEvent?.payload?.persona_name;

  // Locked = feature not usable at all (checklist incomplete)
  if (!allGreen) {
    const failing = (readiness.checks || []).filter((c) => !c.ok).map((c) => c.label);
    return (
      <div
        data-testid="jarvis-autopilot-strip"
        className="rounded-xl border border-slate-200 bg-slate-50/60 px-4 py-3 flex items-start gap-3"
      >
        <span className="mt-0.5 rounded-md bg-slate-200 text-slate-500 p-1.5 shrink-0">
          <Sparkles className="w-3.5 h-3.5" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-[12.5px] font-semibold text-slate-800">
            Jarvis is standing by
          </div>
          <div className="text-[11.5px] text-slate-500 mt-0.5">
            Finish the launch checklist to hand off replies:{" "}
            <span className="font-semibold text-slate-700">{failing.join(" · ") || "—"}</span>
          </div>
        </div>
        <a
          href="/v3/crm/ai-deals"
          data-testid="jarvis-autopilot-setup-link"
          className="rounded-md border border-slate-300 bg-white text-[11.5px] font-semibold text-slate-700 px-2.5 py-1 hover:bg-slate-100 shrink-0"
        >
          Finish setup
        </a>
      </div>
    );
  }

  // Active = green with pulsing dot, primary CTA is Take Over
  if (active) {
    return (
      <div
        data-testid="jarvis-autopilot-strip"
        className="rounded-xl border border-emerald-200 bg-emerald-50/60 px-4 py-3 flex items-start gap-3"
      >
        <span className="mt-0.5 rounded-md bg-emerald-500/10 text-emerald-700 p-1.5 shrink-0 relative">
          <Sparkles className="w-3.5 h-3.5" />
          <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-[12.5px] font-semibold text-emerald-900 inline-flex items-center gap-1.5 flex-wrap">
            <span>Jarvis is replying to this seller</span>
            {persona && (
              <span className="text-[10px] uppercase tracking-widest font-black text-emerald-700/80">
                as {persona}
              </span>
            )}
          </div>
          <div className="text-[11.5px] text-emerald-700/80 mt-0.5">
            Auto-reply is on. Tap Take Over to pause it for this conversation.
          </div>
        </div>
        <button
          type="button"
          disabled={toggling}
          onClick={() => onToggle(false)}
          data-testid="jarvis-take-over-btn"
          className="inline-flex items-center gap-1 rounded-md bg-white text-[11.5px] font-bold text-emerald-800 border border-emerald-300 px-2.5 py-1 hover:bg-emerald-100 disabled:opacity-50 shrink-0"
        >
          {toggling ? <Loader2 className="w-3 h-3 animate-spin" /> : <Hand className="w-3 h-3" />} Take Over
        </button>
      </div>
    );
  }

  // Paused = amber, primary CTA is Resume
  return (
    <div
      data-testid="jarvis-autopilot-strip"
      className="rounded-xl border border-amber-200 bg-amber-50/60 px-4 py-3 flex items-start gap-3"
    >
      <span className="mt-0.5 rounded-md bg-amber-500/10 text-amber-800 p-1.5 shrink-0">
        <Hand className="w-3.5 h-3.5" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-[12.5px] font-semibold text-amber-900">
          You&rsquo;ve taken over this conversation
        </div>
        <div className="text-[11.5px] text-amber-800/80 mt-0.5">
          Jarvis is silent on this lead. Resume anytime — thread history is preserved.
        </div>
      </div>
      <button
        type="button"
        disabled={toggling}
        onClick={() => onToggle(true)}
        data-testid="jarvis-resume-btn"
        className="inline-flex items-center gap-1 rounded-md text-white text-[11.5px] font-bold px-2.5 py-1 hover:opacity-90 disabled:opacity-50 shrink-0"
        style={{ backgroundImage: "linear-gradient(135deg, #F59E0B, #B45309)" }}
      >
        {toggling ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />} Resume Jarvis
      </button>
    </div>
  );
}
